/* eslint-disable react/prop-types */

import React, { useMemo, useState, useEffect } from 'react';
import axios from 'axios';
import classNames from 'classnames';
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  Button,
  Input,
  InputGroup,
  InputGroupAddon,
  InputGroupText,
  Dropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem
} from 'reactstrap';
import { FiSearch, FiFilter, FiMapPin } from 'react-icons/fi';
import { LuStarOff } from 'react-icons/lu';
import { BsBuildings } from 'react-icons/bs';
import { FaListOl } from 'react-icons/fa6';
import { FaStar, FaRegSave } from 'react-icons/fa';
import { GrRevert } from 'react-icons/gr';
import { toast, ToastContainer } from 'react-toastify';
import { getToken } from '../../services/auth';
import { useLoading } from '../../providers/LoadingProvider';

const allStates = [
  { id: 0, sigla: 'ALL', nome: 'Todos os estados' },
  { id: 1, sigla: 'AC', nome: 'Acre' },
  { id: 2, sigla: 'AL', nome: 'Alagoas' },
  { id: 3, sigla: 'AP', nome: 'Amapá' },
  { id: 4, sigla: 'AM', nome: 'Amazonas' },
  { id: 5, sigla: 'BA', nome: 'Bahia' },
  { id: 6, sigla: 'CE', nome: 'Ceará' },
  { id: 7, sigla: 'DF', nome: 'Distrito Federal' },
  { id: 8, sigla: 'ES', nome: 'Espírito Santo' },
  { id: 9, sigla: 'GO', nome: 'Goiás' },
  { id: 10, sigla: 'MA', nome: 'Maranhão' },
  { id: 11, sigla: 'MT', nome: 'Mato Grosso' },
  { id: 12, sigla: 'MS', nome: 'Mato Grosso do Sul' },
  { id: 13, sigla: 'MG', nome: 'Minas Gerais' },
  { id: 14, sigla: 'PA', nome: 'Pará' },
  { id: 15, sigla: 'PB', nome: 'Paraíba' },
  { id: 16, sigla: 'PR', nome: 'Paraná' },
  { id: 17, sigla: 'PE', nome: 'Pernambuco' },
  { id: 18, sigla: 'PI', nome: 'Piauí' },
  { id: 19, sigla: 'RJ', nome: 'Rio de Janeiro' },
  { id: 20, sigla: 'RN', nome: 'Rio Grande do Norte' },
  { id: 21, sigla: 'RS', nome: 'Rio Grande do Sul' },
  { id: 22, sigla: 'RO', nome: 'Rondônia' },
  { id: 23, sigla: 'RR', nome: 'Roraima' },
  { id: 24, sigla: 'SC', nome: 'Santa Catarina' },
  { id: 25, sigla: 'SP', nome: 'São Paulo' },
  { id: 26, sigla: 'SE', nome: 'Sergipe' },
  { id: 27, sigla: 'TO', nome: 'Tocantins' }
];

const legends = [
  { color: '#3C83F6', label: 'Fila Prioritária' },
  { color: '#E1E7EF', label: 'Fila Normal' }
];

export default function ControleFilaPrioritaria() {
  const client = localStorage.getItem('client')
    ? JSON.parse(localStorage.getItem('client'))
    : '';
  const [inicialShoppings, setInicialShoppings] = useState([]);
  const [search, setSearch] = useState('');
  const [isStateOpen, setIsStateOpen] = useState(false);
  const [stateFilter, setStateFilter] = useState('ALL');
  const [shoppings, setShoppings] = useState([]);
  const [isSaving, setIsSaving] = useState(false);
  const { show, hide } = useLoading();
  const [canEdit, setCanEdit] = useState(false);

  useEffect(() => {
    const { CancelToken } = axios;
    const source = CancelToken.source();
    async function fetchMyAPI() {
      try {
        show();
        const apiUrlShopping = `${process.env.REACT_APP_WAS_KEY}/shopping-control`;
        const apiUrlUser = `${process.env.REACT_APP_WAS_KEY}/clientConfig`;

        const response = await axios.get(apiUrlUser, {
          headers: {
            Authorization: `Bearer ${getToken()}`
          },
          cancelToken: source.token
        });

        const res = await axios.get(apiUrlShopping, {
          headers: {
            Authorization: `Bearer ${getToken()}`,
            'x-api-key': response.data.client.owner
          },
          cancelToken: source.token
        });

        const data = res.data.map((d) => ({
          ...d,
          isPriority: !!d.isPriority
        }));
        setCanEdit(!!response.data.user.canManageShoppingQueuePriority);
        setShoppings(data);
        setInicialShoppings(data.map((x) => ({ ...x })));
      } catch (error) {
        setShoppings([]);
        setInicialShoppings([]);
        throw error;
      } finally {
        hide();
      }
    }
    fetchMyAPI();
    return () => {
      source.cancel();
    };
  }, [client.owner, show, hide]);

  function getStateLabel(sigla) {
    return (
      allStates.find((uf) => uf.sigla === sigla)?.nome || 'Todos os estados'
    );
  }

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const result = shoppings.filter((i) => {
      const matchesText =
        !q ||
        `${i.name} ${i.city} ${i.state} ${i.code}`.toLowerCase().includes(q);
      const matchesState = stateFilter === 'ALL' || i.state === stateFilter;
      return matchesText && matchesState;
    });

    return result.sort((a, b) => {
      const stateCompare = a.state.localeCompare(b.state);
      if (stateCompare !== 0) return stateCompare;
      return a.name.localeCompare(b.name);
    });
  }, [shoppings, search, stateFilter]);

  const priorityList = filtered.filter((i) => i.isPriority);
  const normalList = filtered.filter((i) => !i.isPriority);

  const counts = {
    total: filtered.length,
    priority: priorityList.length,
    normal: normalList.length
  };

  function togglePriority(id) {
    setShoppings((prev) =>
      prev.map((i) => (i.id === id ? { ...i, isPriority: !i.isPriority } : i))
    );
  }

  const hasChanges = useMemo(() => {
    if (inicialShoppings.length !== shoppings.length) return true;
    const initMap = new Map(inicialShoppings.map((i) => [i.id, i.isPriority]));
    return shoppings.some((s) => initMap.get(s.id) !== s.isPriority);
  }, [inicialShoppings, shoppings]);

  async function save() {
    try {
      setIsSaving(true);
      show();
      const updates = shoppings
        .filter((atual) => {
          const antigo = inicialShoppings.find((o) => o.id === atual.id);
          return antigo && antigo.isPriority !== atual.isPriority;
        })
        .map((atual) => ({
          id: atual.id,
          isPriority: atual.isPriority
        }));

      if (updates.length === 0) {
        toast.info('Nenhuma alteração para salvar.', {
          position: toast.POSITION.TOP_RIGHT,
          closeButton: false,
          hideProgressBar: true,
          theme: 'colored'
        });
        return;
      }
      await axios.put(
        `${process.env.REACT_APP_WAS_KEY}/shopping-control/priority`,
        { updates },
        {
          headers: {
            Authorization: `Bearer ${getToken()}`,
            'x-api-key': client.owner
          }
        }
      );

      toast.success('Configurações salvas com sucesso.', {
        position: toast.POSITION.TOP_RIGHT,
        closeButton: false,
        hideProgressBar: true,
        theme: 'colored'
      });
      setInicialShoppings(shoppings.map((x) => ({ ...x })));
    } catch (error) {
      toast.error('Ocorreu um erro ao salvar as configurações.', {
        position: toast.POSITION.TOP_RIGHT,
        closeButton: false,
        hideProgressBar: true,
        theme: 'colored'
      });
      throw error;
    } finally {
      hide();
      setIsSaving(false);
    }
  }

  function restore() {
    setShoppings(inicialShoppings.map((x) => ({ ...x })));
  }

  function ItemRow({ shop }) {
    return (
      <Card
        className={classNames('item-row p-0', {
          priority: shop.isPriority
        })}
      >
        <CardBody
          className={classNames('item-body', { priority: shop.isPriority })}
          style={{ borderRadius: '12px' }}
        >
          <div className="item-left">
            <div>
              <div className="item-name">
                {shop.isPriority ? (
                  <FaStar className="icon-priority mr-2 blue" />
                ) : (
                  <LuStarOff className="icon-normal mr-2" />
                )}
                {shop.name}
              </div>
              <div className="item-city">
                <FiMapPin /> {shop.city} - {shop.state}
              </div>
            </div>
          </div>
          {canEdit && (
            <div className="item-right">
              <div className="switch-block">
                <div className="switch-label">
                  {shop.isPriority ? 'Prioritário' : 'Normal'}
                </div>
                <div className="custom-control custom-switch cursor-pointer">
                  <input
                    type="checkbox"
                    className="custom-control-input"
                    id={`priority-${shop.id}`}
                    checked={!!shop.isPriority}
                    onChange={() => togglePriority(shop.id)}
                  />
                  <label
                    className="custom-control-label"
                    htmlFor={`priority-${shop.id}`}
                  />
                </div>
              </div>
            </div>
          )}
        </CardBody>
      </Card>
    );
  }

  return (
    <>
      <ToastContainer />
      <Container fluid className="controle-fila">
        <div className="page-header">
          <div className="header-icon">📑</div>
          <div>
            <h4>Controle de Fila Prioritária</h4>
            <p>
              Selecione quais shoppings entram na fila de leitura prioritária
            </p>
          </div>
        </div>

        <Card className="filters p-0">
          <CardBody className="card-filter-queues">
            <Row className="align-items-center">
              <Col md={8}>
                <InputGroup>
                  <InputGroupAddon addonType="prepend">
                    <InputGroupText
                      style={{
                        backgroundColor: '#F8FAFC',
                        borderColor: '#E1E7EF',
                        color: '#65758B'
                      }}
                    >
                      <FiSearch />
                    </InputGroupText>
                  </InputGroupAddon>
                  <Input
                    placeholder="Buscar shopping..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="input-filter-form"
                  />
                </InputGroup>
              </Col>
              <Col md={4}>
                <Dropdown
                  isOpen={isStateOpen}
                  toggle={() => setIsStateOpen((v) => !v)}
                  className="m-0"
                >
                  <DropdownToggle
                    caret
                    color="light"
                    className="btn-custom-filter"
                  >
                    <FiFilter className="mr-2" /> {getStateLabel(stateFilter)}
                  </DropdownToggle>
                  <DropdownMenu>
                    {allStates.map((uf) => (
                      <DropdownItem
                        key={uf.sigla}
                        onClick={() => setStateFilter(uf.sigla)}
                      >
                        {uf.nome}
                      </DropdownItem>
                    ))}
                  </DropdownMenu>
                </Dropdown>
              </Col>
              <Col md={12} className="mt-3">
                <div className="d-flex flex-row gap-3">
                  {legends.map((item) => (
                    <div
                      key={item.label}
                      className="d-flex align-items-center mr-3"
                      style={{ color: '#65758B' }}
                    >
                      <span
                        style={{
                          display: 'inline-block',
                          width: 12,
                          height: 12,
                          borderRadius: '50%',
                          backgroundColor: item.color,
                          marginRight: 8
                        }}
                      />
                      {item.label}
                    </div>
                  ))}
                </div>
              </Col>
            </Row>
          </CardBody>
        </Card>

        <Row className="stats">
          <Col md={4}>
            <Card className="stat p-0">
              <CardBody className="card-filter-queues stat-body priority">
                <div>
                  <div>Fila Prioritária</div>
                  <h3>{counts.priority}</h3>
                </div>
                <FaStar className="blue" />
              </CardBody>
            </Card>
          </Col>
          <Col md={4}>
            <Card className="stat p-0">
              <CardBody className="card-filter-queues stat-body">
                <div>
                  <div>Fila Normal</div>
                  <h3>{counts.normal}</h3>
                </div>
                <FaListOl />
              </CardBody>
            </Card>
          </Col>
          <Col md={4}>
            <Card className="stat p-0">
              <CardBody className="card-filter-queues stat-body">
                <div>
                  <div>Total de Shoppings</div>
                  <h3>{counts.total}</h3>
                </div>
                <BsBuildings />
              </CardBody>
            </Card>
          </Col>
        </Row>

        {canEdit && (
          <Row>
            <Col md={12} className="mt-3">
              <div className="d-flex flex-row gap-4">
                <Button
                  color="primary"
                  className="custom-button-queues mr-4"
                  onClick={() => save()}
                  disabled={!hasChanges || isSaving}
                >
                  {' '}
                  <FaRegSave /> Salvar Configurações
                </Button>
                <Button
                  color="primary"
                  className="custom-button-queues revert"
                  onClick={() => restore()}
                  disabled={!hasChanges || isSaving}
                >
                  <GrRevert />
                  Reverter Configurações
                </Button>
              </div>
            </Col>
          </Row>
        )}

        <div className="list-section list-priority">
          <Row>
            <Col md={6}>
              <h4>
                <FaStar className="blue" /> Fila Prioritária
              </h4>
            </Col>
            <Col md={6} className="d-flex justify-content-end">
              <h4>
                <small>{priorityList.length} shoppings</small>
              </h4>
            </Col>
          </Row>
          <hr />
          {priorityList.length === 0 && (
            <p>Nenhum shopping em prioridade no momento.</p>
          )}

          <Row>
            {priorityList.map((s) => (
              <Col md={6} className="mb-2">
                <ItemRow key={s.id} shop={s} />
              </Col>
            ))}
          </Row>
        </div>

        <div className="list-section">
          <Row>
            <Col md={6}>
              <h4>
                <FaListOl /> Fila Normal
              </h4>
            </Col>
            <Col md={6} className="d-flex justify-content-end">
              <h4>
                <small>{normalList.length} shoppings</small>
              </h4>
            </Col>
          </Row>
          <hr />
          {normalList.length === 0 && (
            <p>Nenhum shopping na fila normal no momento.</p>
          )}
          <Row>
            {normalList.map((s) => (
              <Col md={6} className="mb-2">
                <ItemRow key={s.id} shop={s} />
              </Col>
            ))}
          </Row>
        </div>
      </Container>
    </>
  );
}
